import React, { createContext, useContext, useState, useReducer } from 'react';

const BookingContext = createContext();

const initialState = {
  step: 0,
  userId: null, // Global userId calculated once and used in all steps
  selectedServices: [],
  selectedDoctor: null, // Changed from selectedStaff map to single doctor
  selectedDate: null,
  selectedTime: null,
  selectedSession: null, // Store full session object for appointment validation
  sessionDate: null, // Date selected in StaffSelection step
  instituteBranchId: null, // Store institute branch ID for session fetching
  sessionRef: null, // Store BookingEngineRef from session for appointment sync
  patientId: null, // Store patient ID for appointment creation
  appointmentDetails: null, // Store completed appointment details
  validatedApiData: null, // Store validated API key data (WebUserId, InstituteId, InstituteBranchId, etc.)
  usingLoggedPatient: false, // Flag to indicate if using logged-in patient from Redux
  otpToken: null, // Store OTP verification token for API calls
  deviceToken: null, // Store device authentication token (used for Patient/Save, Bill/Post, etc.)
  availableSessions: [], // Store fetched sessions from doctor
  existingPatients: [], // Store existing patient records for verified mobile number
  themeColors: null, // Store dynamic theme colors from API
  customerInfo: {
    title: '',
    firstName: '',
    lastName: '',
    phone: '',
    nic: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    address: '',
    occupation: '',
    maritalStatus: ''
  },
  isVerified: false
};

function bookingReducer(state, action) {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.payload };
    case 'ADD_SERVICE':
      return { ...state, selectedServices: [...state.selectedServices, action.payload] };
    case 'REMOVE_SERVICE':
      return { ...state, selectedServices: state.selectedServices.filter(s => s.Id !== action.payload) };
    case 'SET_SERVICES':
      return { ...state, selectedServices: Array.isArray(action.payload) ? action.payload : [] };
    case 'SET_DOCTOR':
      return { 
        ...state, 
        selectedDoctor: action.payload,
        selectedServices: []
      };
    case 'SET_DATE_TIME':
      return { 
        ...state, 
        selectedDate: action.payload.date, 
        selectedTime: action.payload.time,
        selectedSession: action.payload.session,
        sessionRef: action.payload.sessionRef || null
      };
    case 'SET_SESSION_DATE':
      return { ...state, sessionDate: action.payload };
    case 'SET_INSTITUTE_BRANCH':
      return { ...state, instituteBranchId: action.payload };
    case 'SET_AVAILABLE_SESSIONS':
      return { ...state, availableSessions: action.payload };
    case 'SET_EXISTING_PATIENTS':
      return { ...state, existingPatients: Array.isArray(action.payload) ? action.payload : [] };
    case 'SET_CUSTOMER_INFO':
      return { ...state, customerInfo: { ...state.customerInfo, ...action.payload } };
    case 'SET_PATIENT_ID':
      return { ...state, patientId: action.payload };
    case 'SET_APPOINTMENT_DETAILS':
      return { ...state, appointmentDetails: action.payload };
    case 'SET_VERIFIED':
      return { ...state, isVerified: action.payload };
    case 'SET_OTP_TOKEN':
      return { ...state, otpToken: action.payload };
    case 'SET_DEVICE_TOKEN':
      return { ...state, deviceToken: action.payload };
    case 'SET_USER_ID':
      return { ...state, userId: action.payload };
    case 'SET_VALIDATED_API_DATA':
      return { ...state, validatedApiData: action.payload };
    case 'SET_USING_LOGGED_PATIENT':
      return { ...state, usingLoggedPatient: action.payload };
    case 'SET_THEME_COLORS':
      return { ...state, themeColors: action.payload };
    case 'RESTORE_BOOKING_STATE': {
      const payload = action.payload || {};
      return {
        ...state,
        ...payload,
        customerInfo: {
          ...initialState.customerInfo,
          ...state.customerInfo,
          ...(payload.customerInfo || {})
        }
      };
    }
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export function BookingProvider({ children }) {
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  const nextStep = () => dispatch({ type: 'SET_STEP', payload: state.step + 1 });
  const prevStep = () => dispatch({ type: 'SET_STEP', payload: Math.max(0, state.step - 1) });

  return (
    <BookingContext.Provider value={{ state, dispatch, nextStep, prevStep }}>
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  return useContext(BookingContext);
}
